import { engineComponent } from "neo-common";
import { Communication } from "neo-games";
import { BaseController } from "./BaseController";
import { Paylines } from "../Paylines";
import { PaylinesSelector } from "../PaylinesSelector";
import { Symbols } from "../Symbols";
import { WinAnimator } from "../WinAnimator";
import { Machine } from "../Machine";
import { SwitchElement } from "../../Components/Elements/Graphics/SwitchElement";
import { ContainerElement } from "../../Components/Elements/Graphics/ContainerElement";
import { ComponentTypes } from "../../Components/ComponentTypes";
import { WinAnimatorExtended } from "../../CustomComponents/WinAnimatorExtended";
import { GameManager } from "../../Managers/GameManager";
import { FrameTimerEventEmitter, CreateFrameTimer, FrameTimerEvents } from "../../Utils/FrameTimer";
import { PlayList } from "../../Components/Sounds/PlayList";
import { Tweens } from "../../Components/Effects/Tweens";
import { ImageElement } from "../../Components/Elements/Graphics/ImageElement";
import { ModuleManager } from "../../Managers/ModuleManager";
import { StageController } from "../../Components/StageController";
import { DataManager } from "../../Managers/DataManager";
import { GroupTweens } from "../../CustomComponents/GroupTweens";
import { AmbientBackground } from "../../Utils/AmbientBackground";
import { MachineExtended } from "../../CustomComponents/MachineExtended";
import { SymbolExtended } from "../../CustomComponents/SymbolExtended";
import { Scaling } from "../../Utils/Scaling";

type IWin = Communication.Game.IWin;
var GameModules = Communication.Game.GameModules;

@engineComponent(ComponentTypes.DisplayObject)
export class BaseView extends ContainerElement {
    static INTRO_DELAY: number = 60;
    static OUTRO_DELAY: number = 60;
    static OUTRO_DELAY_TO_GAMBLE: number = 0;
    static OUTRO_DELAY_TO_GAMBLE_IN_HIGHWINS: number = 60;
    static INTRO_DELAY_FROM_GAMBLE: number = 30;
    static OUTRO_DELAY_IN_HIGHWINS: number = 90;

    private _controller: BaseController;
    private _wasFinished: boolean = false;
    private _introDelay: FrameTimerEventEmitter;
    private _outroDelay: FrameTimerEventEmitter;

    private _paylines: Paylines;

    public static Quickstopped: boolean = false;

    private _symbols: Symbols;
    private _machine: MachineExtended;
    private _noWinContainer: ContainerElement;
    private _winContainer: ContainerElement;
    private _winAnimator: WinAnimatorExtended;
    private _groupTweens: SwitchElement;

    private modeSetting: { Landscape?: Node; Portrait?: Node; Extended?: Node } = {};

    constructor() {
        super();

        this._introDelay = CreateFrameTimer("introDelay");
        this._introDelay.on(FrameTimerEvents.Complete, this.introFinished.bind(this));

        this._outroDelay = CreateFrameTimer("outroDelay");
        this._outroDelay.on(FrameTimerEvents.Complete, this.outroFinished.bind(this));

        StageController.baseStageContainer = this;
    }

    create(node: Element): void {
        super.create(node);

        if (this.childs != null) {
            for (var key in this.childs) {
                if (this.childs[key] instanceof Paylines) {
                    this._paylines = this.childs[key];
                }

                if (this.childs[key] instanceof Symbols) {
                    this._symbols = this.childs[key];
                    this.removeChild(this._symbols);
                }

                if (this.childs[key] instanceof MachineExtended) {
                    this._machine = this.childs[key];
                }

                if (this.childs[key] instanceof WinAnimator) {
                    this._winAnimator = this.childs[key];
                }

                if (key === "NoWinContainer" && this.childs[key] instanceof ContainerElement) {
                    this._noWinContainer = this.childs[key];
                }

                if (key === "WinContainer" && this.childs[key] instanceof ContainerElement) {
                    this._winContainer = this.childs[key];
                }

                if (key === "GroupTweens" && this.childs[key] instanceof SwitchElement) {
                    this._groupTweens = this.childs[key]; // SwitchElement with 2 elements of GroupTweens
                }
            }

            // set childs to GroupTweens object
            if (this._groupTweens) {
                let groupTweenElements: Object = this._groupTweens.getChildElements();
                if (groupTweenElements) {
                    for (let key in groupTweenElements) {
                        if (groupTweenElements.hasOwnProperty(key)) {
                            if (groupTweenElements[key] instanceof GroupTweens) {
                                (<GroupTweens>groupTweenElements[key]).setChilds(this.childs);
                            }
                        }
                    }
                }
            }

            if (this._symbols == null) {
                if (DEBUG) {
                    this.log("ERROR no symbols");
                }
            }

            if (this._machine == null) {
                if (DEBUG) {
                    this.log("ERROR no machine");
                }
            }

            if (this._paylines == null) {
                if (DEBUG) {
                    this.log("WARNING no paylines");
                }
            }

            if (this._winAnimator == null) {
                if (DEBUG) {
                    this.log("WARNING no winAnimator");
                }
            }
        }

        for (let i = 0; i < node.childNodes.length; i++) {
            const child = node.childNodes[i];
            if (child.nodeName === "Landscape") {
                this.modeSetting.Landscape = child;
            }
            if (child.nodeName === "Portrait") {
                this.modeSetting.Portrait = child;
            }
            if (child.nodeName === "Extended") {
                this.modeSetting.Extended = child;
            }
        }

        Scaling.registerResizeCallback(this.setMode.bind(this));
    }

    initReceived(controller: BaseController): void {
        if (DEBUG) {
            this.log("initReceived");
        }

        this._controller = controller;

        if (this._symbols != null) {
            if (this._machine != null) {
                if (GameManager.baseResponse != null) {
                    if (GameManager.baseResponse.initData != null && GameManager.baseResponse.initData.reels != null) {
                        this._machine.on(Machine.ReelStopped, this.onReelStopped.bind(this));
                        this._machine.on(Machine.MachineStopped, this.machineStopped.bind(this));
                        this._machine.initialize(
                            this._symbols,
                            GameManager.baseResponse.initData.reels,
                            this._winAnimator,
                            this.childs["Teasing"],
                            this.childs["NoTrimContainer"],
                        );
                    }

                    if (
                        GameManager.baseResponse.screen != null &&
                        GameManager.baseResponse.screen.reels != null &&
                        GameManager.baseResponse.screen.reels.length !== 0
                    ) {
                        if (this._winAnimator != null) {
                            this._winAnimator.on(
                                WinAnimator.FinishWinCalculation,
                                this.finishWinCalculation.bind(this),
                            );
                            this._winAnimator.on(WinAnimator.FinishWinAnimator, this.finishWinAnimator.bind(this));
                            this._winAnimator.initialize(
                                this._symbols,
                                this._paylines,
                                this._noWinContainer,
                                this._winContainer,
                                this,
                            );
                            this._winAnimator.showSymbols(GameManager.baseResponse.screen.reels);
                        }
                        this._machine.setStoppedSymbolsArray(GameManager.baseResponse.screen.reels);
                    } else {
                        if (DEBUG) {
                            this.log("ERROR no reels in response");
                        }
                    }
                } else {
                    if (DEBUG) {
                        this.log("ERROR no base reponse");
                    }
                }
            } else {
                if (DEBUG) {
                    this.log("ERROR no machine");
                }
            }
        } else {
            if (DEBUG) {
                this.log("ERROR no symbols");
            }
        }

        if (ModuleManager.gambleActive()) {
            this.visible = true;
            if (this._groupTweens) {
                this._groupTweens.goToCase(GroupTweens.CaseGambleInit);
            }
        } else {
            this.visible = false;
        }
    }

    initIntro(): void {
        StageController.baseStage = true;

        // this.allSwitchElementToCase("INIT_INTRO");

        if (GameManager.gameResponse.totalWin > 0) {
            if (GameManager.baseResponse.currentWin == 0) {
                //init state after fs
                PlayList.loop("SND_BaseRecapLoop");
            }
        } else {
            PlayList.loop("SND_BaseLoop");
        }

        this._winAnimator.showInitWins(GameManager.baseResponse.wins, GameManager.baseResponse.currentWin);
        this.visible = true;

        if (GameManager.baseResponse.wins.length == 0) {
            PlayList.play("SND_GameIntro");
            (<WinAnimatorExtended>this._winAnimator).setActiveElementCaseArray(["GAME_INTRO"]);
        }
    }

    collect(): void {
        PlayList.stop("SND_BaseRecapLoop");
        PlayList.resetVolume("SND_BaseLoop");
        PlayList.loop("SND_BaseLoop");

        this._winAnimator.reset(true);
    }

    spin(): void {
        PlayList.stop("SND_BaseRecapLoop");
        PlayList.resetVolume("SND_BaseLoop");
        PlayList.loop("SND_BaseLoop");

        this._winAnimator.hideExpandWilds(true);

        this._machine.spinStart(this._winAnimator.reset(true, true), true, this._winAnimator.currentSymbols);
    }

    stop(): void {
        this._machine.spinStop(null, true);
    }

    spinReceived(): void {
        this._machine.setStoppedSymbolsArray(GameManager.baseResponse.screen.reels);
        this._machine.spinStop(null, false, true);
    }

    onReelStopped(): void {
        this._winAnimator.onReelStopped(GameManager.baseResponse.screen.reels, this._machine.actualReelStopped);
    }

    public getRandom(min: number, max: number): number {
        return Math.random() * (max - min) + min;
    }

    machineStopped(): void {
        this._wasFinished = false;
        this._controller.machineStopped();

        if (this._winAnimator.showWins(GameManager.baseResponse.wins, GameManager.baseResponse.currentWin)) {
            // PlayList.stop('SND_BaseLoop');
        }
    }

    finishWinCalculation(): void {
        this._wasFinished = true;
        this._controller.winCalculationFinished();
    }

    finishWinAnimator(): void {
        if (!DataManager.Autoplay) {
            // PlayList.stop('SND_BaseLoop');
            // PlayList.loop('SND_BaseRecapLoop');
        }

        this._controller.allWinsFinished();
    }

    resetWinAnimator(): void {
        this._winAnimator.reset(true);
    }

    intro(long: boolean = false, previousModule: string): void {
        StageController.baseStage = true;

        if (GameManager.gameResponse.totalWin > 0) {
            //fs return
            PlayList.stop("SND_BaseLoop");
            PlayList.loop("SND_BaseRecapLoop");
            PlayList.resetVolume("SND_BaseRecapLoop");
        } else {
            PlayList.stop("SND_BaseRecapLoop");
            PlayList.loop("SND_BaseLoop");
            PlayList.resetVolume("SND_BaseLoop");
        }

        let delayIntro: number = BaseView.INTRO_DELAY;
        if (previousModule && this._groupTweens) {
            if (previousModule === GameModules.Gamble) {
                this._groupTweens.goToCase(GroupTweens.CaseGambleToBase);
                if (this._winAnimator.isWildWin) {
                    AmbientBackground.changeAmbientBackground("color-1", 0, 0);
                    this._winAnimator.setActiveElementCaseArray(["SHOW_81_INIT"]);
                }
                delayIntro = BaseView.INTRO_DELAY_FROM_GAMBLE;
            } else {
                this._groupTweens.goToCase(GroupTweens.CaseIntro);
            }
        }

        this.visible = true;
        this._introDelay.start(delayIntro);
    }

    introFinished(): void {
        StageController.baseStage = true;
        this._controller.introFinished();
    }

    outro(): void {
        let wasFinished: boolean = this._wasFinished;
        let inHighWins: boolean = this._winAnimator.reset(true);

        PlayList.stop("SND_BaseLoop", 30);
        PlayList.stop("SND_BaseRecapLoop", 30);

        if (ModuleManager.gambleActive()) {
            AmbientBackground.changeAmbientBackground("base", 200, 500);
            this._winAnimator.setActiveElementCaseArray(["GAMBLE"]);

            if (this._groupTweens) {
                this._groupTweens.goToCase(GroupTweens.CaseBaseToGamble);
            }

            if (inHighWins && !wasFinished) {
                this._outroDelay.start(BaseView.OUTRO_DELAY_TO_GAMBLE_IN_HIGHWINS);
            } else {
                this._outroDelay.start(BaseView.OUTRO_DELAY_TO_GAMBLE);
            }
        } else {
            if (wasFinished) {
                this.outroFinished();
            } else {
                if (inHighWins) {
                    this._outroDelay.start(BaseView.OUTRO_DELAY_IN_HIGHWINS);
                } else {
                    this._outroDelay.start(BaseView.OUTRO_DELAY);
                }
            }
        }
    }

    outroFinished(): void {
        this._outroDelay.stop();

        this._controller.outroFinished();
    }

    private allSwitchElementToCase(caseType: string): void {
        for (let key in this.childs) {
            if (this.childs[key] instanceof SwitchElement) {
                let elem: SwitchElement = this.childs[key];

                if (elem.hasCase(caseType)) {
                    elem.goToCase(caseType);
                }
            }
        }
    }

    disableBase(): void {
        this._winAnimator.resetTimer();
    }

    /**
     * Toggle all sounds used in base game (background, effects, .etc)
     * @param enable
     */
    toggleSounds(enable: boolean): void {
        if (enable) {
            PlayList.loop("SND_BaseLoop");
        } else {
            PlayList.stop("SND_BaseLoop");
            PlayList.stop("SND_BaseRecaploop");
        }
    }

    showMessage(type: "NORMAL" | "END" | "RETRIGGER" | "FORCE" = "NORMAL"): void {}

    hideMessage(): void {}

    public calculationCounter(actualCounterValue: string): void {
        if (this._winAnimator) {
            this._winAnimator.calculationCounter(actualCounterValue);
        }
    }

    private setMode = function (mode: "landscape" | "portrait" | "extended") {
        switch (mode) {
            case "extended":
                if (this.modeSetting.Extended) {
                    this.updateByNode(this.modeSetting.Extended);
                    break;
                }
            case "portrait":
                if (this.modeSetting.Portrait) {
                    this.updateByNode(this.modeSetting.Portrait);
                    break;
                }
            case "landscape":
                if (this.modeSetting.Landscape) {
                    this.updateByNode(this.modeSetting.Landscape);
                }
                break;
        }
    };
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/GameComponents/Base/BaseView.ts
 **/